import { premiumCards } from '@app/constants/premiumCards';
import { GROUPS } from '@app/constants/GROUPS';
import {
    PRECONFIGURED_DEFAULT_PROCESS_DURATION_EMPTY,
    PRECONFIGURED_DEFAULT_PROCESS_DURATION_INDEFINIDO,
    PRECONFIGURED_DEFAULT_PROCESS_DURATION_INDEFINIDO_LABEL,
    preconfiguredDefaultProcessResponsibleId,
    preconfiguredSubSkeleton
} from './preconfigured-premium-flow-defaults';
import {
    PREMIUM_FLOW_PROCESS_ICON_URL_COMPLETADO,
    PREMIUM_FLOW_PROCESS_ICON_URL_COTIZACION,
    PREMIUM_FLOW_PROCESS_ICON_URL_COTIZACION_COUPA,
    PREMIUM_FLOW_PROCESS_ICON_URL_ENTREGA,
    PREMIUM_FLOW_PROCESS_ICON_URL_FACTURA,
    PREMIUM_FLOW_PROCESS_ICON_URL_ORDEN_DE_COMPRA
} from '@app/constants/PREMIUM_FLOW_PROCESS_ICON_URLS';
import {
    RESPONSIBLE_ORDER_KEY,
    responsibleOrderIdByKey
} from '@app/constants/RESPONSIBLES_ORDER';
import {
    OrderAction,
    PREMIUM_ORDER_ACTIONS
} from '@app/constants/PREMIUM_ORDER_ACTIONS';
import { ROLES_GROUPS } from '@app/constants/ROLES_GROUPS';
import {
    MessageSubprocess,
    NotificationSubprocesses,
    OrderCards,
    OrderProcessStructure,
    OrderSubprocessStructure
} from '@app/core/services/api/API.service';

/** Igual que {@link CardDetailPremiumProcessPermissionsComponent#setDefaultConfig}: tarjetas visibles para roles por defecto. */
export function buildDefaultPremiumProcessCards(): OrderCards[] {
    return premiumCards.map(premiumCard => ({
        __typename: 'OrderCards' as const,
        id: premiumCard.id,
        name: premiumCard.name,
        permissions: premiumCard.default.map((rol: string) => ({
            __typename: 'OrderCardPermissions' as const,
            idCard: premiumCard.id,
            rol,
            visible: true,
            thirdContent: null
        }))
    }));
}

/** Igual que {@link SetDefaultMessagesNotificationsSubprocessesComponent} `buildDefaultMessages`. */
export function buildDefaultMessagesForPremiumAction(
    actionId: number
): MessageSubprocess[] {
    const actionConfig = PREMIUM_ORDER_ACTIONS.find(poa => poa.id === actionId);
    if (!actionConfig) {
        return ROLES_GROUPS.ORDER_GROUP.map(
            (rol: string) =>
                ({
                    rol,
                    description: '',
                    visibleAction: false
                } as MessageSubprocess)
        );
    }
    return ROLES_GROUPS.ORDER_GROUP.map(
        (rol: string) =>
            ({
                rol,
                visibleAction: actionConfig.defaultPermissions.includes(rol),
                description: actionConfig.defaultPermissions.includes(rol)
                    ? actionConfig.enabledMessage
                    : actionConfig.disbledMessage
            } as MessageSubprocess)
    );
}

/** Igual que {@link SetDefaultMessagesNotificationsSubprocessesComponent} `buildDefaultNotifications`. */
export function buildDefaultNotificationsForPremiumAction(
    actionId: number
): NotificationSubprocesses[] {
    const actionConfig = PREMIUM_ORDER_ACTIONS.find(poa => poa.id === actionId);
    const defaultGroups = actionConfig?.defaultNotifications ?? [];
    return Object.keys(GROUPS)
        .map(key => (GROUPS as Record<string, string>)[key])
        .map((group: string) => ({
            group,
            enabledEmail: defaultGroups.includes(group),
            enabledPopUp: defaultGroups.includes(group)
        }))
        .filter(
            item => item.enabledEmail || item.enabledPopUp
        ) as NotificationSubprocesses[];
}

/**
 * Grupos que siempre quedan con email y popup activos en el primer subproceso
 * (position 1) del primer proceso de cada flujo precargado, aunque la acción
 * no los incluya en `defaultNotifications`.
 */
const MANDATORY_NOTIFICATION_GROUPS_FIRST_PROCESS_STEP_ONE: readonly string[] = [
    GROUPS.MANAGEMENT,
    GROUPS.ADMIN
];

function mergeNotificationsWithMandatoryManagementAndAdmin(
    notifications: ReadonlyArray<NotificationSubprocesses | null> | null
): NotificationSubprocesses[] {
    const byGroup = new Map<string, NotificationSubprocesses>();
    for (const n of notifications ?? []) {
        if (n?.group) {
            byGroup.set(n.group, {
                __typename: 'NotificationSubprocesses' as const,
                group: n.group,
                enabledEmail: !!n.enabledEmail,
                enabledPopUp: !!n.enabledPopUp
            });
        }
    }
    for (const group of MANDATORY_NOTIFICATION_GROUPS_FIRST_PROCESS_STEP_ONE) {
        byGroup.set(group, {
            __typename: 'NotificationSubprocesses' as const,
            group,
            enabledEmail: true,
            enabledPopUp: true
        });
    }
    return Object.values(GROUPS)
        .map(group => byGroup.get(group))
        .filter(
            (n): n is NotificationSubprocesses =>
                !!n && (!!n.enabledEmail || !!n.enabledPopUp)
        );
}

/**
 * En el **primer proceso** del flujo, subprocesos con `position === 1` fuerzan
 * notificación a Dirección y Administrador (ver {@link GROUPS}).
 */
export function applyMandatoryManagementAndAdminNotificationsForPositionOne(
    subprocesses: OrderSubprocessStructure[]
): OrderSubprocessStructure[] {
    return subprocesses.map(sub =>
        sub.position === 1
            ? {
                  ...sub,
                  notifications: mergeNotificationsWithMandatoryManagementAndAdmin(
                      sub.notifications ?? null
                  )
              }
            : sub
    );
}

export interface PreconfiguredProcessSubSkeleton {
    action: number;
    position: number;
    /** Si se omite, se usa el `name` de {@link PREMIUM_ORDER_ACTIONS}. */
    name?: string;
    descripcion?: string;
}

export interface PreconfiguredProcessSkeleton {
    name: string;
    position: number;
    descripcion?: string;
    duration?: string;
    responsible?: number;
    /** URL absoluta del ícono (p. ej. {@link PREMIUM_FLOW_PROCESS_ICON_URL_COTIZACION}). */
    imageUrl?: string | null;
    subprocesses: PreconfiguredProcessSubSkeleton[];
}

function orderActionOrFallback(actionId: number): OrderAction | undefined {
    return PREMIUM_ORDER_ACTIONS.find(a => a.id === actionId);
}

export function buildSubprocessStructuresFromSkeleton(
    processId: string,
    subprocessDefs: ReadonlyArray<PreconfiguredProcessSubSkeleton>
): OrderSubprocessStructure[] {
    return subprocessDefs.map(subDef => {
        const cfg = orderActionOrFallback(subDef.action);
        const subId = crypto.randomUUID();
        return {
            __typename: 'OrderSubprocessStructure' as const,
            id: subId,
            name: subDef.name ?? cfg?.name ?? '',
            descripcion: subDef.descripcion ?? cfg?.name ?? '',
            action: subDef.action,
            position: subDef.position,
            process: processId,
            madeBy: null,
            madeAt: null,
            messages: buildDefaultMessagesForPremiumAction(subDef.action),
            notifications: buildDefaultNotificationsForPremiumAction(
                subDef.action
            )
        };
    });
}

/**
 * Construye `OrderProcessStructure[]` a partir de nombre/posición/responsable/imagen
 * por proceso, tarjetas por defecto (`premiumCards`) y subprocesos solo con `action`
 * + posición (mensajes y notificaciones desde `PREMIUM_ORDER_ACTIONS`).
 */
export function buildOrderProcessesFromSkeleton(
    defs: ReadonlyArray<PreconfiguredProcessSkeleton>
): OrderProcessStructure[] {
    const processIds = defs.map(() => crypto.randomUUID());
    return defs.map((def, i) => {
        const pid = processIds[i];
        let subprocesses = buildSubprocessStructuresFromSkeleton(
            pid,
            def.subprocesses
        );
        if (i === 0) {
            subprocesses = applyMandatoryManagementAndAdminNotificationsForPositionOne(
                subprocesses
            );
        }
        return {
            __typename: 'OrderProcessStructure' as const,
            id: pid,
            name: def.name,
            descripcion: def.descripcion ?? def.name,
            duration:
                def.duration ?? PRECONFIGURED_DEFAULT_PROCESS_DURATION_EMPTY,
            position: def.position,
            responsible:
                def.responsible ?? preconfiguredDefaultProcessResponsibleId(),
            madeBy: null,
            madeAt: null,
            image:
                def.imageUrl != null && def.imageUrl !== ''
                    ? {
                          __typename: 'File' as const,
                          key: null,
                          url: def.imageUrl
                      }
                    : null,
            cards: buildDefaultPremiumProcessCards(),
            subprocesses
        };
    });
}

/** Flujo Coupa — misma secuencia de procesos y acciones que la plantilla previa en JSON. */
export const COUPA_FLOW_SKELETON: PreconfiguredProcessSkeleton[] = [
    {
        name: 'Cotización',
        descripcion:
            'Etapa inicial: checkout en Coupa y registro de la orden de compra emitida en Coupa para continuar el flujo.',
        duration: PRECONFIGURED_DEFAULT_PROCESS_DURATION_INDEFINIDO,
        position: 1,
        responsible: responsibleOrderIdByKey(RESPONSIBLE_ORDER_KEY.MARKETING),
        imageUrl: PREMIUM_FLOW_PROCESS_ICON_URL_COTIZACION_COUPA,
        subprocesses: [
            { action: 7, position: 1, name: 'Checkout Coupa' },
            { action: 5, position: 2, name: 'Carga de OC Coupa' }
        ]
    },
    {
        name: 'Orden de compra',
        descripcion:
            'Seguimiento de la OC: se documentan y cargan las evidencias de entrega del bien o servicio contratado.',
        duration: PRECONFIGURED_DEFAULT_PROCESS_DURATION_INDEFINIDO,
        position: 2,
        responsible: responsibleOrderIdByKey(RESPONSIBLE_ORDER_KEY.MARKETING),
        imageUrl: PREMIUM_FLOW_PROCESS_ICON_URL_ORDEN_DE_COMPRA,
        subprocesses: [
            { action: 2, position: 1, name: 'Carga evidencias de entrega' }
        ]
    },
    {
        name: 'Entrega',
        descripcion:
            'Recepción y validación documental: carga de factura por el proveedor y revisión para asegurar que coincide con la entrega.',
        duration: PRECONFIGURED_DEFAULT_PROCESS_DURATION_INDEFINIDO,
        position: 3,
        responsible: responsibleOrderIdByKey(RESPONSIBLE_ORDER_KEY.PROVEEDORES),
        imageUrl: PREMIUM_FLOW_PROCESS_ICON_URL_ENTREGA,
        subprocesses: [
            { action: 15, position: 1, name: 'Subir factura' },
            { action: 8, position: 2, name: 'Validar factura' }
        ]
    },
    {
        name: 'Factura',
        descripcion:
            'Cierre contable del pedido: se confirma la orden como completada una vez validada la facturación.',
        duration: PRECONFIGURED_DEFAULT_PROCESS_DURATION_INDEFINIDO_LABEL,
        position: 4,
        responsible: responsibleOrderIdByKey(
            RESPONSIBLE_ORDER_KEY.BRAND_FACTORY
        ),
        imageUrl: PREMIUM_FLOW_PROCESS_ICON_URL_FACTURA,
        subprocesses: [{ action: 13, position: 1, name: 'Completar orden' }]
    },
    {
        name: 'Completado',
        descripcion:
            'Estado final del flujo Coupa: el proceso queda archivado y notificado como terminado sin pasos pendientes.',
        duration: PRECONFIGURED_DEFAULT_PROCESS_DURATION_INDEFINIDO,
        position: 5,
        responsible: responsibleOrderIdByKey(
            RESPONSIBLE_ORDER_KEY.BRAND_FACTORY
        ),
        imageUrl: PREMIUM_FLOW_PROCESS_ICON_URL_COMPLETADO,
        subprocesses: [{ action: 16, position: 1, name: 'Proceso finalizado' }]
    }
];

/**
 * Primer proceso del híbrido Coupa: transferencia, Coupa, Clip y validaciones.
 * Posiciones repetidas (1/1/1 y 2/2/2) como en operación; nombres y mensajes desde
 * `PREMIUM_ORDER_ACTIONS`. El front elige subproceso según tipo de pedido.
 */
export const HIBRIDO_COUPA_FIRST_PROCESS_SUB_SKELETON: PreconfiguredProcessSubSkeleton[] = [
    preconfiguredSubSkeleton(31, 1),
    preconfiguredSubSkeleton(7, 1),
    preconfiguredSubSkeleton(28, 1),
    preconfiguredSubSkeleton(5, 2),
    preconfiguredSubSkeleton(32, 2),
    preconfiguredSubSkeleton(29, 2)
];

/**
 * Primer proceso de TDC/transferencia: mismos subprocesos que {@link HIBRIDO_COUPA_FIRST_PROCESS_SUB_SKELETON}
 * sin las acciones Coupa (checkout 7 y carga de OC 5).
 */
export const TDC_TRANSFER_FIRST_PROCESS_SUB_SKELETON: PreconfiguredProcessSubSkeleton[] = HIBRIDO_COUPA_FIRST_PROCESS_SUB_SKELETON.filter(
    sub => sub.action !== 7 && sub.action !== 5
);

/** Flujo OC — plantilla previa en JSON. */
export const OC_FLOW_SKELETON: PreconfiguredProcessSkeleton[] = [
    {
        name: 'Cotización',
        descripcion:
            'Cotización aprobada: se formaliza la orden cargando el documento de orden de compra (OC) correspondiente.',
        duration: PRECONFIGURED_DEFAULT_PROCESS_DURATION_EMPTY,
        position: 1,
        responsible: responsibleOrderIdByKey(RESPONSIBLE_ORDER_KEY.MARKETING),
        imageUrl: PREMIUM_FLOW_PROCESS_ICON_URL_COTIZACION,
        subprocesses: [{ action: 30, position: 1, name: 'Carga de OC' }]
    },
    {
        name: 'Orden de compra',
        descripcion:
            'Ejecución de la OC: registro de acuses de entrega que acreditan la recepción del proveedor o del cliente.',
        duration: PRECONFIGURED_DEFAULT_PROCESS_DURATION_EMPTY,
        position: 2,
        responsible: responsibleOrderIdByKey(
            RESPONSIBLE_ORDER_KEY.BRAND_FACTORY
        ),
        imageUrl: PREMIUM_FLOW_PROCESS_ICON_URL_ORDEN_DE_COMPRA,
        subprocesses: [
            { action: 2, position: 1, name: 'Carga acuses de entrega' }
        ]
    },
    {
        name: 'Entrega',
        descripcion:
            'Facturación asociada a la entrega: el proveedor sube la factura y se valida contra los acuses y la OC.',
        duration: PRECONFIGURED_DEFAULT_PROCESS_DURATION_EMPTY,
        position: 3,
        responsible: responsibleOrderIdByKey(
            RESPONSIBLE_ORDER_KEY.BRAND_FACTORY
        ),
        imageUrl: PREMIUM_FLOW_PROCESS_ICON_URL_ENTREGA,
        subprocesses: [
            { action: 15, position: 1, name: 'Subir factura' },
            { action: 8, position: 2, name: 'Validar factura' }
        ]
    },
    {
        name: 'Factura',
        descripcion:
            'Último paso operativo: se marca la orden como completada cuando la factura y la entrega están alineadas.',
        duration: PRECONFIGURED_DEFAULT_PROCESS_DURATION_INDEFINIDO_LABEL,
        position: 4,
        responsible: responsibleOrderIdByKey(
            RESPONSIBLE_ORDER_KEY.BRAND_FACTORY
        ),
        imageUrl: PREMIUM_FLOW_PROCESS_ICON_URL_FACTURA,
        subprocesses: [{ action: 13, position: 1, name: 'Completar orden' }]
    },
    {
        name: 'Completado',
        descripcion:
            'Flujo OC cerrado: no quedan acciones pendientes y el expediente queda registrado como finalizado.',
        duration: PRECONFIGURED_DEFAULT_PROCESS_DURATION_EMPTY,
        position: 5,
        responsible: responsibleOrderIdByKey(
            RESPONSIBLE_ORDER_KEY.BRAND_FACTORY
        ),
        imageUrl: PREMIUM_FLOW_PROCESS_ICON_URL_COMPLETADO,
        subprocesses: [{ action: 16, position: 1, name: 'Proceso finalizado' }]
    }
];

/**
 * Variante de {@link OC_FLOW_SKELETON}: en el primer proceso («Cotización»),
 * subprocesos de autorización (11 y opcionalmente 24) **antes** de la carga de
 * OC (30). La posición de «Carga de OC» pasa a 2 o 3 según el número de pasos
 * de autorización.
 */
export function buildOcFlowWithAuthorizationSkeleton(
    doubleAuthorization: boolean
): PreconfiguredProcessSkeleton[] {
    const ocUploadPosition = doubleAuthorization ? 3 : 2;
    const cotizacionSubprocesses: PreconfiguredProcessSubSkeleton[] = doubleAuthorization
        ? [
              preconfiguredSubSkeleton(11, 1),
              preconfiguredSubSkeleton(24, 2),
              preconfiguredSubSkeleton(30, ocUploadPosition)
          ]
        : [
              preconfiguredSubSkeleton(11, 1),
              preconfiguredSubSkeleton(30, ocUploadPosition)
          ];
    return OC_FLOW_SKELETON.map((proc, index) => {
        if (index !== 0) {
            return proc;
        }
        return {
            ...proc,
            descripcion:
                'Cotización: autorización(es) de compras y, a continuación, carga del documento de orden de compra (OC).',
            subprocesses: cotizacionSubprocesses
        };
    });
}

/** Flujo TDC (carrito: transferencia o tarjeta Clip) — plantilla previa en JSON. */
export const TDC_TRANSFER_FLOW_SKELETON: PreconfiguredProcessSkeleton[] = [
    {
        name: 'Pago del pedido',
        descripcion:
            'Transferencia (comprobante y validación), pago con Clip y validación del pago con Clip antes de seguir con la preparación del pedido.',
        duration: PRECONFIGURED_DEFAULT_PROCESS_DURATION_EMPTY,
        position: 1,
        responsible: responsibleOrderIdByKey(RESPONSIBLE_ORDER_KEY.MARKETING),
        imageUrl: PREMIUM_FLOW_PROCESS_ICON_URL_COTIZACION,
        subprocesses: [...TDC_TRANSFER_FIRST_PROCESS_SUB_SKELETON]
    },
    {
        name: 'Revisión de pago',
        descripcion:
            'Tras validar el pago: se adjuntan evidencias de entrega que respaldan que el pedido llegó al cliente.',
        duration: PRECONFIGURED_DEFAULT_PROCESS_DURATION_EMPTY,
        position: 2,
        responsible: responsibleOrderIdByKey(
            RESPONSIBLE_ORDER_KEY.BRAND_FACTORY
        ),
        imageUrl: PREMIUM_FLOW_PROCESS_ICON_URL_ORDEN_DE_COMPRA,
        subprocesses: [
            { action: 2, position: 1, name: 'Subir comprobante de entrega' }
        ]
    },
    {
        name: 'Entrega',
        descripcion:
            'Producto o servicio entregado: factura del proveedor y validación para conciliar con la entrega y el pago.',
        duration: PRECONFIGURED_DEFAULT_PROCESS_DURATION_EMPTY,
        position: 3,
        responsible: responsibleOrderIdByKey(RESPONSIBLE_ORDER_KEY.PROVEEDORES),
        imageUrl: PREMIUM_FLOW_PROCESS_ICON_URL_ENTREGA,
        subprocesses: [
            { action: 15, position: 1, name: 'Subir factura' },
            { action: 8, position: 2, name: 'Validar Factura' }
        ]
    },
    {
        name: 'Factura',
        descripcion:
            'Cierre del ciclo TDC/transfer: completar la orden cuando la factura y los comprobantes están en regla.',
        duration: PRECONFIGURED_DEFAULT_PROCESS_DURATION_INDEFINIDO_LABEL,
        position: 4,
        responsible: responsibleOrderIdByKey(
            RESPONSIBLE_ORDER_KEY.BRAND_FACTORY
        ),
        imageUrl: PREMIUM_FLOW_PROCESS_ICON_URL_FACTURA,
        subprocesses: [{ action: 13, position: 1, name: 'Completar orden' }]
    },
    {
        name: 'Completado',
        descripcion:
            'Proceso premium con transferencia finalizado; el pedido queda archivado sin tareas abiertas.',
        duration: PRECONFIGURED_DEFAULT_PROCESS_DURATION_EMPTY,
        position: 5,
        responsible: responsibleOrderIdByKey(
            RESPONSIBLE_ORDER_KEY.BRAND_FACTORY
        ),
        imageUrl: PREMIUM_FLOW_PROCESS_ICON_URL_COMPLETADO,
        subprocesses: [{ action: 16, position: 1, name: 'Proceso finalizado' }]
    }
];
